/** Path-scoped so connect-src allows this bucket, not every bucket. Named per
 *  environment: a hardcoded one blocks the upload in all the others. */
export function attestationDocumentsUrl(environment: string | undefined) {
  if (!environment) {
    return ''
  }

  return `https://s3.us-east-1.amazonaws.com/moment-attestation-documents-${environment}/`
}

/** Offering-doc uploads presign against this bucket. Path-style, matching
 *  horizon's S3 client — going virtual-hosted would move the bucket into the
 *  host and drop the path scoping. */
export function userContentUrl(environment: string | undefined) {
  if (!environment) {
    return ''
  }

  return `https://s3.us-east-1.amazonaws.com/moment-user-content-${environment}/`
}

export function schemaUploadsUrl(environment: string | undefined) {
  if (!environment) {
    return ''
  }

  return `https://s3.us-east-1.amazonaws.com/moment-horizon-schema-uploads-${environment}/`
}

function externalQuotesUrl(environment: string | undefined) {
  if (!environment) {
    return ''
  }

  return `https://s3.us-east-1.amazonaws.com/moment-creative-planning-venue-quotes-${environment}/`
}

export const MomentEnv = {
  ApiUrl: process.env.NEXT_PUBLIC_MOMENT_API_ROOT,

  SormApiUrl: process.env.NEXT_PUBLIC_SORM_API_ROOT,

  Environment: process.env.NEXT_PUBLIC_ENVIRONMENT,

  // Origin of the standalone pdf-viewer app, embedded cross-origin as an
  // <iframe> (see @moment/pdf-viewer-ui). Defaults to the local
  // dev server so local web-ui embeds the local viewer.
  PdfViewerOrigin:
    process.env.NEXT_PUBLIC_PDF_VIEWER_ORIGIN ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:4300' : ''),

  AttestationDocumentsUrl: attestationDocumentsUrl(
    process.env.NEXT_PUBLIC_ENVIRONMENT,
  ),

  UserContentUrl: userContentUrl(process.env.NEXT_PUBLIC_ENVIRONMENT),

  SchemaUploadsUrl: schemaUploadsUrl(process.env.NEXT_PUBLIC_ENVIRONMENT),

  ExternalQuotesUrl: externalQuotesUrl(process.env.NEXT_PUBLIC_ENVIRONMENT),

  isDevelopment: process.env.NODE_ENV === 'development',

  AgGridTestIdsEnabled: process.env.NEXT_PUBLIC_ENVIRONMENT !== 'prod',

  AgGridLicenseKey: process.env.NEXT_PUBLIC_AG_GRID_ENTERPRISE_LICENSE_KEY,

  PylonAppId: process.env.NEXT_PUBLIC_PYLON_APP_ID,

  StatsigClientKey: process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY,
  StatsigEnvironment: process.env.NEXT_PUBLIC_STATSIG_ENVIRONMENT,
  // Authoritative, build-time baked. Sourced from the server-only
  // `STATSIG_DISABLED` env via the `env:` block in `next.config.js` — no
  // `NEXT_PUBLIC_*` prefix because the decision itself doesn't belong to the
  // browser, just the resulting literal. When `true`, Statsig is off even
  // if a client key happens to be set; when `false`, a missing key is a
  // misconfiguration and the providers throw rather than silently degrade.
  StatsigDisabled: process.env.STATSIG_DISABLED === 'true',

  DataDogApplicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
  DataDogClientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  DataDogService: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
  DataDogEnv: process.env.NEXT_PUBLIC_DATADOG_ENV,

  LogrocketAppId: process.env.NEXT_PUBLIC_LOGROCKET_APP_ID,

  GitHash: getGitHash(),

  OneSchemaClientId: process.env.NEXT_PUBLIC_ONE_SCHEMA_CLIENT_ID,
  OneSchemaProposalHoldingsTemplateKey:
    process.env.NEXT_PUBLIC_ONE_SCHEMA_TEMPLATE_KEY_PROPOSAL_HOLDINGS,

  WorkOSPasswordResetUrl: process.env.NEXT_PUBLIC_WORKOS_PASSWORD_RESET_URL,
  OrganizationLogoUrl:
    process.env.NEXT_PUBLIC_ORGANIZATION_LOGO_URL || '/moment-logo.svg',
  OrganizationName: process.env.NEXT_PUBLIC_ORGANIZATION_NAME || 'Moment',
  /**
   * Until we add the ability to choose a specific WorkOS organization to route the login/SSO
   * we need to hard-code one.
   *
   * You can find the organization ID in the WorkOS dashboard: https://dashboard.workos.com/get-started
   * See app/auth/README.md for more information
   */
  DefaultOrganization: null, // < Fill me in >
}

/**
 * Gets the current git hash from environment variables.
 * The git hash is generated at build time by the scripts/generate-git-hash.js script.
 */
function getGitHash(): string {
  return process.env.NEXT_PUBLIC_GIT_HASH || ''
}
